import { generateUuid } from "../../utils/id";
import type { TendonUUID } from "../../types/scene";
import type { ParsedActuatorsMap } from "./types";

type ActuatorParser = {
  xmlDoc: Document;
  actuatorTypeDefaults: Record<string, Record<string, unknown> | undefined>;
  actuatorClassParentMap: Record<string, string | undefined>;
  actuatorClassDefaults: Record<string, Record<string, unknown> | undefined>;
  jointNameToRange: Record<string, [number, number] | undefined>;
  extractActuatorAttrs: (node: Element) => Record<string, unknown>;
};

const tryExtractStableActuatorUuid = (actuatorName: string): string | null => {
  const m = String(actuatorName || "")
    .trim()
    .match(/^act_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
  return m && m[1] ? m[1] : null;
};

export const parseActuators = (
  rawParser: unknown,
  tendonNameToUuid: Record<string, TendonUUID> = {}
): { actuators: ParsedActuatorsMap } => {
  const parser = rawParser as ActuatorParser;
  const actuators: ParsedActuatorsMap = {};
  const actuatorRoot = parser.xmlDoc.querySelector("actuator");
  if (!actuatorRoot) return { actuators };

  const children = Array.from(actuatorRoot.children) as Element[];
  children.forEach((node: Element) => {
    const type = node.tagName.toLowerCase();
    const name = node.getAttribute("name") || `${type}_${Object.keys(actuators).length}`;
    const joint = node.getAttribute("joint") || undefined;
    const cls = node.getAttribute("class");

    let meta: Record<string, unknown> = {};

    const typeDefaults = parser.actuatorTypeDefaults[type];
    if (typeDefaults) {
      meta = { ...meta, ...typeDefaults };
    }

    const mergeDefaultsFromClass = (klass: string | null) => {
      if (!klass) return;
      const chain: string[] = [];
      let cur: string | undefined | null = klass;
      while (cur) {
        chain.unshift(cur);
        cur = parser.actuatorClassParentMap[cur];
      }
      chain.forEach((k) => {
        const d = parser.actuatorClassDefaults[k];
        if (d) meta = { ...meta, ...d };
      });
    };
    if (cls) mergeDefaultsFromClass(cls);

    meta = { ...meta, ...parser.extractActuatorAttrs(node) };

    if (!meta.ctrlrange && meta.inheritrange && joint) {
      const jointRange = parser.jointNameToRange[joint];
      if (jointRange) {
        const scale = meta.inheritrange as number;
        meta.ctrlrange = [jointRange[0] * scale, jointRange[1] * scale] as [number, number];
      }
    }

    const stableUuid = tryExtractStableActuatorUuid(name);
    const tendonName = typeof meta?.tendon === "string" ? meta.tendon : undefined;
    const mappedTendonUuid = tendonName ? tendonNameToUuid[tendonName] : undefined;
    actuators[name] = {
      uuid: stableUuid || generateUuid(),
      name,
      joint,
      tendonUuid: mappedTendonUuid,
      ...meta,
      kind: type,
    };
  });

  return { actuators };
};
